import {
  Accessor,
  Component,
  createContext,
  createEffect,
  createSignal,
  JSXElement,
  mergeProps,
  onCleanup,
  Setter,
  untrack,
  useContext
} from 'solid-js';
import { PaginationState } from '@tanstack/solid-table';
import {
  useQuery,
  useQueryClient,
  UseQueryResult
} from '@tanstack/solid-query';

import { createStore } from 'solid-js/store';
import { miscConstants } from '~/consts.ts';
import { Params, useParams } from '@solidjs/router';
import { cloneObject } from '~/utils/commonUtils.ts';

export enum PaginationTypes {
  PAGE_NAVIGATION = 'PAGE_NAVIGATION',
  LOAD_MORE = 'LOAD_MORE'
}

type PageDetails = {
  prevPageToken: string;
  nextPageToken: string;
  totalSize: number;
  currentPage: number;
  pageToken: string;
  data: any[];
};

type PaginationContextValueType = {
  paginationType: PaginationTypes;
  dataParser: (data: any) => any[]; // @todo fix typing with generics
  pageDetails: PageDetails;
  pagination: Accessor<PaginationState>;
  queryObject: UseQueryResult<any, any>;
  setPagination: Setter<PaginationState>;
  pageSize: number;
  toNextPage: () => void;
  toPrevPage: () => void;
  reset: () => void;
};

type DataFetcherFunctionReturn = {
  queryKey: string[];
  queryFn: () => Promise<any>;
  retry: (failureCount: any, error: any) => boolean;
};

type DataFetcherFunction = (
  pageDetails: () => { pageToken: string; params: Params }
) => DataFetcherFunctionReturn;

const PaginationContext = createContext<PaginationContextValueType>();

type PaginationContextProviderProps = {
  pageSize: number;
  fetcher: DataFetcherFunction;
  dataParser: (data: any) => any[]; // @todo fix typing with generics
  children: JSXElement;
  paginationType?: PaginationTypes;
};

export default function PaginationContextProvider(
  props: PaginationContextProviderProps
): JSXElement {
  const merged = mergeProps(
    {
      pageSize: miscConstants.DEFAULT_TABLE_PAGE_SIZE,
      paginationType: PaginationTypes.PAGE_NAVIGATION
    },
    props
  );

  const params = useParams();
  const queryClient = useQueryClient();

  const [pagination, setPagination] = createSignal<PaginationState>({
    pageIndex: 0,
    pageSize: merged.pageSize
  });

  const [pageDetails, setPageDetails] = createStore<PageDetails>({
    prevPageToken: '',
    nextPageToken: '',
    totalSize: 0,
    currentPage: -1,
    pageToken: '',
    data: []
  });

  // Resetting the query is important before creating the query
  const currentPageParams = cloneObject(params);
  const options = merged.fetcher(() => ({
    pageToken: pageDetails.pageToken,
    params: currentPageParams
  }));

  queryClient.removeQueries({
    queryKey: options.queryKey,
    type: 'all',
    exact: true
  });

  const queryObject = useQuery(() => ({
    ...merged.fetcher(() => ({
      pageToken: pageDetails.pageToken,
      params
    }))
  }));

  createEffect(() => {
    if (pagination().pageIndex > -1) {
      untrack(async () => {
        // Set the page token to be sent to the api
        // based on the prev or next navigation
        setPageDetails(
          'pageToken',
          pagination().pageIndex < pageDetails.currentPage
            ? pageDetails.prevPageToken
            : pageDetails.nextPageToken
        );
        queryObject.refetch();
      });
    }
  });

  createEffect(() => {
    if (!queryObject.isLoading && queryObject.data) {
      untrack(() => {
        const data = queryObject.data;
        // Somehow in a page which does not contain data
        // eg. last record in the last page is deleted
        // we need to navigate to the previous page if it existis
        // else move to the first page
        if (data.totalSize > 0 && merged.dataParser(data).length === 0) {
          let gotoPage = pagination().pageIndex - 1;
          if (!pageDetails.prevPageToken) {
            setPageDetails('prevPageToken', '');
            setPageDetails('nextPageToken', '');
            gotoPage = 0;
          }
          setPagination((pagination) => ({
            ...pagination,
            pageIndex: gotoPage
          }));
          return;
        }
        setPageDetails('prevPageToken', data.prevPageToken);
        setPageDetails('nextPageToken', data.nextPageToken);
        setPageDetails('totalSize', data.totalSize);
        setPageDetails('currentPage', pagination().pageIndex);
        if (merged.paginationType === PaginationTypes.PAGE_NAVIGATION) {
          setPageDetails('data', merged.dataParser(queryObject.data) ?? []);
        } else if (merged.paginationType === PaginationTypes.LOAD_MORE) {
          setPageDetails('data', (data) => {
            return [...data, ...(merged.dataParser(queryObject.data) ?? [])];
          });
        }
      });
    }
  });

  onCleanup(() => {
    // Even though we are removing the queries during the component inititalization
    // lets remove on cleanup as well,
    // so that the page number does not cached
    // if this query is used in some other component
    queryClient.removeQueries({
      queryKey: options.queryKey,
      type: 'all',
      exact: true
    });
  });

  return (
    <PaginationContext.Provider
      value={{
        paginationType: merged.paginationType,
        dataParser: merged.dataParser,
        pagination,
        pageDetails,
        queryObject,
        pageSize: merged.pageSize,
        setPagination,
        toNextPage: () => {
          setPagination((pagination) => ({
            ...pagination,
            pageIndex: pagination.pageIndex + 1
          }));
          if (merged.paginationType === PaginationTypes.PAGE_NAVIGATION) {
            window.scrollTo({
              top: 0,
              behavior: 'smooth'
            });
          }
        },
        toPrevPage: () => {
          setPagination((pagination) => ({
            ...pagination,
            pageIndex: pagination.pageIndex - 1
          }));
          if (merged.paginationType === PaginationTypes.PAGE_NAVIGATION) {
            window.scrollTo({
              top: 0,
              behavior: 'smooth'
            });
          }
        },
        reset: () => {
          setPageDetails('prevPageToken', '');
          setPageDetails('nextPageToken', '');
          setPageDetails('currentPage', -1);
          setPageDetails('pageToken', '');
          setPageDetails('data', []);
          setPagination((pagination) => ({
            ...pagination,
            pageIndex: 0
          }));
        }
      }}
    >
      {merged.children}
    </PaginationContext.Provider>
  );
}

const usePaginationContext = (): PaginationContextValueType =>
  useContext(PaginationContext) as PaginationContextValueType;

function withPagination(
  component: Component,
  {
    pageSize = miscConstants.DEFAULT_TABLE_PAGE_SIZE,
    fetcher,
    dataParser,
    paginationType
  }: {
    pageSize?: number;
    fetcher: DataFetcherFunction;
    dataParser: (data: any) => any[]; // @todo fix typing with generics
    paginationType?: PaginationTypes;
  }
) {
  const Renderer = component;
  return () => (
    <PaginationContextProvider
      pageSize={pageSize}
      fetcher={fetcher}
      dataParser={dataParser}
      paginationType={paginationType}
    >
      <Renderer />
    </PaginationContextProvider>
  );
}

export { usePaginationContext, withPagination };
